import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "@/store/DataContext";
import {
  CurrencySegment,
  Field,
  GhostButton,
  NumberInput,
  PrimaryButton,
  TextArea,
  TextInput,
} from "@/components/base/FormControls";
import SelectSheet, { type SelectOption } from "@/components/base/SelectSheet";
import { consumeLots } from "@/lib/inventory";
import { money, number, todayISO, uid } from "@/lib/format";
import type { Currency, Sale, SaleItem } from "@/lib/types";

interface SaleDraft {
  key: string;
  productId: string;
  quantity: string;
  unitPrice: string;
}

function parse(value: string): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function emptyItem(): SaleDraft {
  return { key: uid(), productId: "", quantity: "", unitPrice: "" };
}

const paymentMethods = ["Efectivo", "Transferencia", "Tarjeta", "Crédito"];

export default function NuevaVenta() {
  const { data, stock, addSale } = useData();
  const navigate = useNavigate();

  const [date, setDate] = useState(todayISO());
  const [customer, setCustomer] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Efectivo");
  const [currency, setCurrency] = useState<Currency>(data.settings.presentationCurrency);
  const [discount, setDiscount] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<SaleDraft[]>([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options: SelectOption[] = useMemo(
    () =>
      stock
        .filter((s) => s.quantity > 0)
        .map((s) => ({
          value: s.productId,
          label: s.productName,
          sub: `${number(s.quantity, 0)} ${s.unit} disponibles`,
          trailing: money(s.bestSalePrice, s.currency),
        })),
    [stock],
  );

  const computed = useMemo(() => {
    return items.map((it) => {
      const row = stock.find((s) => s.productId === it.productId);
      const qty = parse(it.quantity);
      const price = parse(it.unitPrice);
      if (!row || qty <= 0) {
        return { key: it.key, row, qty, price, cost: 0, shortage: 0, available: row?.quantity ?? 0 };
      }
      const res = consumeLots(
        data,
        it.productId,
        row.productName,
        qty,
        price,
        currency,
        data.settings.exchangeRate,
      );
      return {
        key: it.key,
        row,
        qty,
        price,
        cost: res.item?.costOfGoods ?? 0,
        shortage: res.shortage,
        available: row.quantity,
      };
    });
  }, [items, stock, data, currency]);

  const totals = useMemo(() => {
    const gross = items.reduce((s, it) => s + parse(it.quantity) * parse(it.unitPrice), 0);
    const income = Math.max(0, gross - parse(discount));
    const cost = computed.reduce((s, c) => s + c.cost, 0);
    return { gross, income, cost, profit: income - cost };
  }, [items, discount, computed]);

  const updateItem = (key: string, patch: Partial<SaleDraft>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };

  const selectProduct = (key: string, productId: string) => {
    const row = stock.find((s) => s.productId === productId);
    updateItem(key, {
      productId,
      unitPrice: row ? String(row.bestSalePrice) : "",
    });
  };

  const save = async () => {
    const valid = items.filter((it) => it.productId && parse(it.quantity) > 0);
    if (valid.length === 0) {
      setError("Selecciona al menos un producto con cantidad.");
      return;
    }
    const over = computed.find((c) => c.shortage > 0);
    if (over && over.row) {
      setError(
        `No hay suficiente inventario de ${over.row.productName}. Disponible: ${number(over.available, 0)}.`,
      );
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const saleItems: SaleItem[] = [];
      valid.forEach((it) => {
        const row = stock.find((s) => s.productId === it.productId);
        if (!row) return;
        const res = consumeLots(
          data,
          it.productId,
          row.productName,
          parse(it.quantity),
          parse(it.unitPrice),
          currency,
          data.settings.exchangeRate,
        );
        if (res.item) saleItems.push(res.item);
      });

      const sale: Sale = {
        id: uid(),
        type: "sale",
        date,
        customer: customer.trim(),
        paymentMethod,
        currency,
        exchangeRate: currency === "USD" ? data.settings.exchangeRate : null,
        discount: parse(discount),
        notes: notes.trim(),
        documentIds: [],
        items: saleItems,
        createdAt: new Date().toISOString(),
      };

      await addSale(sale);
      navigate("/movimientos");
    } catch (err) {
      setError("No se pudo guardar la venta. Intenta de nuevo.");
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const noStock = options.length === 0;

  return (
    <div className="fr-animate space-y-5 pb-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200/80 text-foreground-700"
        >
          <i className="ri-arrow-left-line text-lg" />
        </button>
        <div>
          <h1 className="text-[20px] font-bold text-foreground-950">Registrar venta</h1>
          <p className="text-[12px] text-foreground-500">Descuenta inventario y calcula tu ganancia</p>
        </div>
      </div>

      {noStock ? (
        <div className="flex items-start gap-2 rounded-2xl bg-accent-50 p-3.5">
          <i className="ri-error-warning-line mt-0.5 text-accent-700" />
          <p className="text-[12px] leading-relaxed text-accent-900">
            No tienes productos con existencias. Registra una compra primero para poder vender.
          </p>
        </div>
      ) : null}

      <div className="rounded-3xl border border-background-200/70 bg-background-50 p-4 shadow-card">
        <div className="space-y-3">
          <Field label="Moneda de la operación">
            <CurrencySegment value={currency} onChange={(v) => setCurrency(v as Currency)} />
          </Field>
          <Field label="Fecha">
            <TextInput value={date} onChange={setDate} type="date" icon="ri-calendar-line" />
          </Field>
          <Field label="Cliente (opcional)">
            <TextInput
              value={customer}
              onChange={setCustomer}
              placeholder="Nombre del cliente"
              icon="ri-user-3-line"
            />
          </Field>
          <Field label="Método de pago (opcional)">
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 no-scrollbar">
              {paymentMethods.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPaymentMethod(m)}
                  className={`flex-none rounded-full px-4 py-2 text-[12px] font-medium ${
                    paymentMethod === m
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-200 bg-background-50 text-foreground-600"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </div>

      <h2 className="text-[15px] font-semibold text-foreground-900">Productos vendidos</h2>

      <div className="space-y-3">
        {items.map((it, index) => {
          const info = computed.find((c) => c.key === it.key);
          const over = info ? info.shortage > 0 : false;
          return (
            <div
              key={it.key}
              className="rounded-3xl border border-background-200/70 bg-background-50 p-4 shadow-card"
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-[12px] font-semibold text-primary-700">
                  {index + 1}
                </span>
                {items.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => setItems((prev) => prev.filter((x) => x.key !== it.key))}
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-background-200 text-foreground-500"
                  >
                    <i className="ri-delete-bin-line text-[15px]" />
                  </button>
                ) : null}
              </div>

              <div className="space-y-3">
                <Field label="Producto">
                  <SelectSheet
                    value={it.productId}
                    options={options}
                    placeholder={noStock ? "Sin inventario disponible" : "Selecciona un producto"}
                    title="Selecciona un producto"
                    onChange={(v) => selectProduct(it.key, v)}
                  />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Cantidad">
                    <NumberInput
                      value={it.quantity}
                      onChange={(v) => updateItem(it.key, { quantity: v })}
                      placeholder="0"
                    />
                  </Field>
                  <Field label="Precio unitario">
                    <NumberInput
                      value={it.unitPrice}
                      onChange={(v) => updateItem(it.key, { unitPrice: v })}
                      placeholder="0.00"
                      prefix={currency === "USD" ? "US$" : "RD$"}
                    />
                  </Field>
                </div>

                {over ? (
                  <p className="rounded-xl bg-red-50 px-3 py-2 text-[11px] text-red-600">
                    Existencias insuficientes. Disponible: {number(info?.available ?? 0, 0)}.
                  </p>
                ) : info && info.qty > 0 ? (
                  <div className="flex justify-between rounded-xl bg-background-100 px-3 py-2 text-[11px]">
                    <span className="text-foreground-500">Costo de lo vendido</span>
                    <span className="tabular-nums font-medium text-foreground-800">
                      {money(info.cost, currency)}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <GhostButton
        label="Agregar producto"
        icon="ri-add-line"
        onClick={() => setItems((prev) => [...prev, emptyItem()])}
      />

      <Field label="Descuento (opcional)">
        <NumberInput
          value={discount}
          onChange={setDiscount}
          placeholder="0.00"
          prefix={currency === "USD" ? "US$" : "RD$"}
        />
      </Field>

      <div className="rounded-3xl border border-background-200/70 bg-background-50 p-4 shadow-card">
        <h3 className="mb-3 text-[14px] font-semibold text-foreground-900">Resumen de la venta</h3>
        <div className="space-y-2 text-[13px]">
          <div className="flex justify-between">
            <span className="text-foreground-500">Subtotal</span>
            <span className="tabular-nums font-medium text-foreground-900">
              {money(totals.gross, currency)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-500">Descuento</span>
            <span className="tabular-nums font-medium text-foreground-900">
              -{money(parse(discount), currency)}
            </span>
          </div>
          <div className="flex justify-between border-t border-background-200 pt-2">
            <span className="font-medium text-foreground-700">Total vendido</span>
            <span className="tabular-nums font-semibold text-foreground-950">
              {money(totals.income, currency)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-foreground-500">Costo de los productos vendidos</span>
            <span className="tabular-nums font-medium text-foreground-900">
              -{money(totals.cost, currency)}
            </span>
          </div>
          <div className="flex justify-between border-t border-background-200 pt-2">
            <span className="font-medium text-foreground-700">Ganancia bruta real</span>
            <span className="tabular-nums font-semibold text-primary-700">
              {money(totals.profit, currency)}
            </span>
          </div>
        </div>
      </div>

      <Field label="Notas (opcional)">
        <TextArea value={notes} onChange={setNotes} placeholder="Detalles de la venta..." />
      </Field>

      {error ? (
        <p className="rounded-2xl bg-red-50 px-4 py-3 text-[12px] text-red-600">{error}</p>
      ) : null}

      <PrimaryButton
        label={saving ? "Guardando..." : "Guardar venta"}
        icon="ri-save-3-line"
        onClick={save}
        disabled={saving || noStock}
      />
    </div>
  );
}